import { useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useToast } from '@winnin/winnin_ui.components.toast'

import { useDashboardMutations } from 'hooks-queries/dashboards'
import { useCurrentLanguage, useTranslate } from 'lib/tolgee'
import { analyticsTracker, languagesUtils, TOLGEE_GLOBALS } from 'utils'
import { getRoute } from 'utils/routes'
import { PATHNAMES } from 'utils/routes/consts'

import { CREATE_TERRITORY_ANALYSIS_ERROR_LOG } from './create-niche-dashboard-modal.consts'
import type { ICreateNicheDashboardModalModel } from './create-niche-dashboard-modal.types'
import { useNicheSelection } from '../common/hooks/use-niche-selection'
import { NICHE_MIN_LIMIT } from '../common/niche.consts'

export const useCreateNicheDashboardModalModel = ({
  handleCloseModal,
  nicheList,
  territoryName,
  contextName,
  entityId,
}: ICreateNicheDashboardModalModel) => {
  const [inputValue, setInputValue] = useState(territoryName)

  const { createToast } = useToast()
  const t = useTranslate()
  const currentLanguage = useCurrentLanguage()
  const history = useHistory()

  const [createTerritoryAnalysis, { loading }] = useDashboardMutations({
    mutationName: 'createTerritoryAnalysis',
    errorLog: CREATE_TERRITORY_ANALYSIS_ERROR_LOG,
  })

  const {
    selectedNiches,
    handleSelectNiche,
    effectiveNiches,
    nicheListWithActive,
    isBelowNicheMinLimit,
    nicheTerritoryIds,
  } = useNicheSelection(nicheList)

  const language = useMemo(() => {
    return languagesUtils.locationLanguageToPrimaryLanguage(currentLanguage)
  }, [currentLanguage])

  const onInputChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = event => {
    const value = event.target.value
    setInputValue(value)
  }

  const handleCreateReport = () => {
    if (effectiveNiches.length < NICHE_MIN_LIMIT) {
      return
    }

    const { NICHE_CONTEXT } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(NICHE_CONTEXT.CREATE_DASH, {
      name: inputValue,
      language,
      territories: nicheTerritoryIds,
      contextName,
    })
    createTerritoryAnalysis({
      variables: {
        workspaceJobTerritoryAnalysis: {
          language,
          name: inputValue,
          territories: nicheTerritoryIds,
          contextTitle: contextName,
        },
      },
    })
      .then(res => {
        if (res.data?.createWorkspaceJobTerritoryAnalysis?.__typename === 'WorkspaceJob') {
          const dashboardData = res.data?.createWorkspaceJobTerritoryAnalysis
          const jobId = dashboardData.id
          const workspaceId = dashboardData.workspaceId
          const dashboardUrl = getRoute(PATHNAMES.WORKSPACE_JOBS, {
            jobId,
            panelId: workspaceId,
            tab: 'dashboard',
          })
          history.push(dashboardUrl)
          handleCloseModal()
        }
      })
      .catch(() => {
        createToast({
          content: t(TOLGEE_GLOBALS.somethingWentWrong),
        })
      })
  }

  const showNicheMinLimitAlert = isBelowNicheMinLimit && !loading

  const isSubmitEnabled = Boolean(inputValue?.length) && !loading && !isBelowNicheMinLimit

  const handleCancel = () => {
    const { TOP_CANCEL_CREATE_REPORT } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(TOP_CANCEL_CREATE_REPORT, {
      entityId,
    })
    handleCloseModal()
  }

  return {
    selectedNiches,
    handleSelectNiche,
    inputValue,
    onInputChange,
    handleCreateReport,
    isSubmitEnabled,
    showNicheMinLimitAlert,
    isLoading: loading,
    nicheListWithActive,
    handleCancel,
  }
}
